class CartDiscount extends HTMLElement {
  constructor() {
    super();
    this.activeFetch = null;
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleRemove = this.handleRemove.bind(this);
  }

  connectedCallback() {
    this.form = this.querySelector('.js-cart-discount__form');
    this.input = this.querySelector('.js-cart-discount__input');
    this.errorEl = this.querySelector('.js-cart-discount__error');
    this.errorTextEl = this.errorEl ? this.errorEl.querySelector('.cart-item__error-text') : null;
    this.applyBtn = this.querySelector('.js-cart-discount__apply');
    this.spinnerEl = this.applyBtn ? this.applyBtn.querySelector('.loading__spinner') : null;

    if (this.form) this.form.addEventListener('submit', this.handleSubmit);
    this.addEventListener('click', this.handleRemove);
  }

  setLoading(isLoading) {
    if (!this.applyBtn) return;
    this.applyBtn.classList.toggle('loading', isLoading);
    if (this.spinnerEl) this.spinnerEl.classList.toggle('hidden', !isLoading);
    if (isLoading) {
      this.applyBtn.setAttribute('aria-disabled', 'true');
    } else {
      this.applyBtn.removeAttribute('aria-disabled');
    }
  }

  disconnectedCallback() {
    if (this.activeFetch) this.activeFetch.abort();
  }

  getExistingCodes() {
    const codes = [];
    this.querySelectorAll('.js-cart-discount__pill').forEach((pill) => {
      const code = pill.dataset.discountCode;
      if (code) codes.push(code);
    });
    return codes;
  }

  showError(message) {
    if (!this.errorEl || !this.errorTextEl) return;
    this.errorTextEl.textContent = message;
    this.errorEl.classList.remove('hidden');
  }

  hideError() {
    if (this.errorEl) this.errorEl.classList.add('hidden');
  }

  createAbortController() {
    if (this.activeFetch) this.activeFetch.abort();
    const controller = new AbortController();
    this.activeFetch = controller;
    return controller;
  }

  async postDiscount(codes, signal) {
    const cartItems = document.querySelector('cart-drawer-items');
    const sectionIds = cartItems
      ? cartItems.getSectionsToRender().map((s) => s.section)
      : [this.dataset.sectionId || 'cart-drawer'];

    const response = await fetch(routes.cart_update_url, {
      ...fetchConfig(),
      body: JSON.stringify({
        discount: codes.join(','),
        sections: sectionIds,
      }),
      signal,
    });
    return response.json();
  }

  applySectionUpdate(cartData) {
    const cartItems = document.querySelector('cart-drawer-items');
    if (!cartItems || !cartData.sections) return;

    cartItems.getSectionsToRender().forEach((section) => {
      const container = document.getElementById(section.id);
      if (!container) return;
      const elementToReplace = container.querySelector(section.selector) || container;
      elementToReplace.innerHTML = cartItems.getSectionInnerHTML(
        cartData.sections[section.section],
        section.selector
      );
    });

    const newCartItems = document.querySelector('cart-drawer-items');
    if (newCartItems && typeof newCartItems.cleanupCart === 'function') {
      newCartItems.cleanupCart(cartData);
    }

    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-discount', cartData });
    }
  }

  async handleSubmit(event) {
    event.preventDefault();
    if (!this.input) return;

    const code = this.input.value.trim();
    if (!code) return;

    this.hideError();

    const existing = this.getExistingCodes();
    if (existing.includes(code)) {
      this.input.value = '';
      this.input.blur();
      return;
    }

    const controller = this.createAbortController();
    const sectionId = this.dataset.sectionId || 'cart-drawer';

    this.setLoading(true);

    try {
      const data = await this.postDiscount([...existing, code], controller.signal);

      const submitted = data.discount_codes && data.discount_codes.find((d) => d.code === code);

      if (submitted && submitted.applicable === false) {
        this.input.value = '';
        this.showError(this.dataset.errorCode || 'Enter a valid discount code');
        return;
      }

      const sectionHtml = data.sections && data.sections[sectionId];
      if (sectionHtml) {
        const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
        const newPills = parsed.querySelectorAll('.js-cart-discount__pill');
        const newCodes = Array.from(newPills).map((p) => p.dataset.discountCode).filter(Boolean);

        const sameAsBefore =
          newCodes.length === existing.length &&
          newCodes.every((c) => existing.includes(c));

        if (sameAsBefore && submitted && submitted.applicable === true) {
          this.input.value = '';
          this.showError(this.dataset.errorShipping || 'Shipping discounts will appear at checkout');
          return;
        }

        this.applySectionUpdate(data);
      }
    } catch (error) {
      if (error.name !== 'AbortError') console.error('[cart-discount] apply failed', error);
    } finally {
      if (this.activeFetch === controller) {
        this.activeFetch = null;
        this.setLoading(false);
      }
    }
  }

  async handleRemove(event) {
    const removeBtn = event.target.closest('.js-cart-discount__pill-remove');
    if (!removeBtn) return;
    event.preventDefault();

    const pill = removeBtn.closest('.js-cart-discount__pill');
    if (!pill) return;
    const code = pill.dataset.discountCode;
    if (!code) return;

    const existing = this.getExistingCodes();
    const next = existing.filter((c) => c !== code);

    const controller = this.createAbortController();

    try {
      const data = await this.postDiscount(next, controller.signal);
      this.applySectionUpdate(data);
    } catch (error) {
      if (error.name !== 'AbortError') console.error('[cart-discount] remove failed', error);
    } finally {
      this.activeFetch = null;
    }
  }
}

if (!customElements.get('cart-discount')) {
  customElements.define('cart-discount', CartDiscount);
}
